/**
 * QA 2026-10-01 F-04 — US state and territory codes, and the one normalizer
 * every create, edit and import path uses for a mailing state.
 *
 * Before this, the server stored whatever it received: "al" was saved
 * lowercase and "ZZ" was accepted. `normalizeUsState` uppercases a valid
 * two-letter code, maps a full name ("Texas", "new york") to its code, and
 * returns null for anything else. Blank handling is the caller's job: an empty
 * string also returns null, so callers check for blank first when blank means
 * "leave empty" rather than "invalid".
 */
/** USPS codes: the 50 states, DC, and the five inhabited territories. */
export const US_STATES = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
    DC: 'District of Columbia',
    PR: 'Puerto Rico',
    VI: 'U.S. Virgin Islands',
    GU: 'Guam',
    AS: 'American Samoa',
    MP: 'Northern Mariana Islands',
};
/** The 56 codes, in the order above. */
export const US_STATE_CODES = Object.keys(US_STATES);
const CODE_SET = new Set(US_STATE_CODES);
/** Lowercased, punctuation-free name -> code ("district of columbia" -> "DC"). */
const NAME_TO_CODE = (() => {
    const m = new Map();
    for (const code of US_STATE_CODES)
        m.set(nameKey(US_STATES[code]), code);
    // Common alternate spellings.
    m.set(nameKey('Washington DC'), 'DC');
    m.set(nameKey('Washington D.C.'), 'DC');
    m.set(nameKey('US Virgin Islands'), 'VI');
    m.set(nameKey('Virgin Islands'), 'VI');
    return m;
})();
function nameKey(s) {
    return s
        .toLowerCase()
        .replace(/[.,]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}
/** True when `code` is exactly one of the 56 uppercase codes. */
export function isUsStateCode(code) {
    return CODE_SET.has(code);
}
/**
 * "al" / " AL " / "Alabama" / "alabama" -> "AL". Returns null for anything
 * that is not a US state, DC or territory, and for blank input.
 */
export function normalizeUsState(input) {
    if (typeof input !== 'string')
        return null;
    const trimmed = input.trim();
    if (trimmed === '')
        return null;
    const upper = trimmed.toUpperCase();
    if (CODE_SET.has(upper))
        return upper;
    return NAME_TO_CODE.get(nameKey(trimmed)) ?? null;
}
/** Field message for an invalid state, shared by the server and the forms. */
export const US_STATE_INVALID_MESSAGE = 'Use a two-letter US state code, like AL.';
//# sourceMappingURL=us-states.js.map